import React, { useContext } from "react";
import { clsx } from "clsx";
import { useSafeIntl } from "@bloomberg/media.i18n";

import ErrorBoundary from "~components/ErrorBoundary";
import { GridFeatureTopContent, GridFull } from "~components/GridLayout";
import LedeImage from "~components/Lede/LedeImage";
import LedeVideo from "~components/Lede/LedeVideo";
import SectionIdentifier from "~components/SectionIdentifier";
import {
    ArticleAbstract,
    ArticleDek,
    ArticleHeadline,
} from "~components/shared";
import { Image, Video } from "~types/types";
import { CurrentStoryContext } from "~utils/currentStoryContext";
import { isLanguageCodeJa } from "~utils/i18n/isLanguageCodeJa";

import styles from "./FeatureHeader.module.css";

const SPLIT_CLASSNAME = clsx(styles.media, styles.splitHolder);

export default function FeatureHeader(): JSX.Element {
    const { story } = useContext(CurrentStoryContext);
    const { abstract, headline, hedAndDekPosition, lede, sites, summaryHtml } =
        story;
    const { languageCode } = useSafeIntl();

    const isAbove = hedAndDekPosition === "above";
    const isLeft = hedAndDekPosition === "left";
    const isRight = hedAndDekPosition === "right";
    const isSplit = isLeft || isRight;
    const hasAbstract = Boolean(abstract?.length);

    const isJa = isLanguageCodeJa(languageCode);

    const hed = (
        <GridFeatureTopContent
            className={clsx(styles.hed, {
                [styles.splitHed]: isSplit,
            })}
            data-position={hedAndDekPosition}
        >
            <SectionIdentifier
                className={styles.sectionIdentifier}
                story={story}
            />

            <header
                className={clsx(styles.header, {
                    [styles.headlineIsAboveMediaMargin]: isAbove,
                    [styles.headlineIsBelowMediaMargin]: !isAbove,
                })}
            >
                <ArticleHeadline
                    className={clsx({
                        [styles.featureHeadlineDefaultLocale]: !isJa,
                    })}
                    languageCode={languageCode}
                >
                    {headline}
                </ArticleHeadline>

                {hasAbstract && (
                    <ArticleAbstract
                        abstract={abstract}
                        languageCode={languageCode}
                    />
                )}

                {!hasAbstract && (
                    <ArticleDek
                        className={clsx({
                            [styles.featureDekDefaultLocale]: !isJa,
                        })}
                        dek={summaryHtml}
                        languageCode={languageCode}
                    />
                )}
            </header>
        </GridFeatureTopContent>
    );

    const ledeMediaClassName = clsx(styles.ledeMedia, {
        [styles.splitLede]: isSplit,
        [styles.leftLede]: isLeft,
        [styles.rightLede]: isRight,
    });

    const ledeMedia = (
        <>
            {lede?.type === "image" && (
                <LedeImage
                    lede={lede as Image}
                    className={ledeMediaClassName}
                    isExpandable={false}
                    maxWidth={2200}
                />
            )}

            {lede?.type === "video" && (
                <ErrorBoundary fallback="failed to load Video">
                    <LedeVideo
                        lede={lede as Video}
                        className={ledeMediaClassName}
                        sites={sites}
                    />
                </ErrorBoundary>
            )}
        </>
    );

    if (isSplit && isLeft) {
        return (
            <GridFull className={SPLIT_CLASSNAME}>
                {hed}
                {ledeMedia}
            </GridFull>
        );
    }

    if (isSplit && isRight) {
        return (
            <GridFull className={SPLIT_CLASSNAME}>
                {ledeMedia}
                {hed}
            </GridFull>
        );
    }

    if (isAbove) {
        return (
            <>
                {hed}

                <GridFull
                    className={styles.media}
                    data-position={hedAndDekPosition}
                >
                    {ledeMedia}
                </GridFull>
            </>
        );
    }

    return (
        <>
            <GridFull
                className={styles.media}
                data-position={hedAndDekPosition}
            >
                {ledeMedia}
            </GridFull>

            {hed}
        </>
    );
}
